import React, { Fragment } from 'react';
import { fg } from '@atlaskit/platform-feature-flags';

import { isCustomUI } from '../..';
import type { GQLExtension } from '../../web-client/graphql/types';
import { calculateViewportSizeHeight, type ViewportSizeType } from './calculateViewportSizeHeight';

type ReservedHeightWrapperProps = {
	children: React.ReactNode;
	extension: GQLExtension;
};

export const ReservedHeightWrapper = ({
	extension,
	children,
}: ReservedHeightWrapperProps): JSX.Element => {
	const extensionViewportSize = extension?.properties?.viewportSize as ViewportSizeType | undefined;

	// Only apply reserved height if the feature flag is on AND viewportSize is defined
	if (extensionViewportSize && fg('confluence_forge_early_render_reserve_height')) {
		const viewportSizeHeight = calculateViewportSizeHeight(extensionViewportSize);

		// Custom UI extensions have full support for viewportSize, leading to a strict height.
		// UI Kit 2 extensions, while they can define viewportSize in their manifest, primarily
		// have dynamic sizing. This means viewportSize is not a strict constraint for them as it
		// is for Custom UI. Using minHeight based on a manifest-defined viewportSize is therefore
		// a best-effort approach to proactively reserve space. This aims to reduce layout shift
		// while still allowing the extension to grow dynamically if its content requires more space.
		if (isCustomUI(extension)) {
			return (
				<div style={{ height: viewportSizeHeight }} data-testid="reserved-height-wrapper-custom-ui">
					{children}
				</div>
			);
		} else {
			return (
				<div style={{ minHeight: viewportSizeHeight }} data-testid="reserved-height-wrapper-uikit">
					{children}
				</div>
			);
		}
	}

	// If FF is off, or if viewportSize is undefined, render children directly
	return <Fragment>{children}</Fragment>;
};
